import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";
import { useParams } from "react-router-dom";

import "./FeuE2DataFlowActivities.scss";

import { Button, Label, TextArea } from "abs-ui-components";

import { FeuECommonColumns } from "../../FeuConstants";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import AutoSave from "../../../../../common/AutoSave/AutoSave";
import { AUTOSAVE_SUCCESS_MESSAGE } from "../../../Mrv/Constants";

const FeuE2DataFlowActivities = forwardRef(({ FeuE2DataFlowActivitiesData, refetchMonitoringData,vesselMstId }, ref) => {
    
    const { control, getValues, reset, formState } = useFormContext();
    const { isDirty } = formState;
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    useEffect(() => {
        if (FeuE2DataFlowActivitiesData) {
            reset(prev => ({ ...prev, e2: FeuE2DataFlowActivitiesData }));
        }
    }, [FeuE2DataFlowActivitiesData, reset]);

    AutoSave(ref, [], async () => {
        if (!isDirty) return;
        await handleSave(true);
    });

    const e2Columns = useMemo(() => {
        return FeuECommonColumns
            .filter(col => col.name !== "versionOfProcedure")
            .map(col =>
                col.name === "referenceToProcedure"
                    ? {
                        ...col,
                        label: "Reference for procedure",
                        name: "referenceToProcedure",
                        maxLength: 1000,
                    }
                    : col
            );
    }, []);

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("e2");
        const payload = {
            ...e2Columns.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const FeuE2DataFlowActivities = await callApi({
                method: "POST",
                url: API_ROUTES.E2(vesselMstId),
                data: payload,
            });
            if (FeuE2DataFlowActivities?.isSuccess) {
                emitToasterMsg("success", "Table E.2", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, e2: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table E.2", FeuE2DataFlowActivities?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table E.2", error?.errorMessage);
        }
    };

    return (
        <div className="e2-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of procedure</h6>
                </MDBCol>
                <MDBCol md="4">
                    <h6 className="titles-font">
                        Procedures for data flow activities
                    </h6>
                </MDBCol>
            </MDBRow>

            {e2Columns.map(({ label, name, maxLength, type }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        <Controller
                            name={`e2.${name}`}
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    id={`e2_${name}`}
                                    className="textarea-resize"
                                    value={field.value || ""}
                                    onChange={field.onChange}
                                    placeholder=""
                                    maxLength={maxLength}
                                    rows={1}
                                />
                            )}
                        />
                    </MDBCol>
                </MDBRow>
            ))}

            <div className="text-center">
                <Button id="feue2_save_btn"
                    disabled={!isDirty}
                    onClick={async () => {
                        manualSaveTriggered.current = true;
                        await handleSave(false);
                    }}>
                    Save
                </Button>
            </div>
        </div>
    );
});

export default FeuE2DataFlowActivities;
